import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Image,
  Typography,
  message,
  Affix,
  Select,
  Popconfirm,
  Input,
  Space,
  Form,
  Tooltip,
  Divider,
  DatePicker,
  Upload,
  Alert,
} from "antd";
import {
  FilePdfOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import Utilities from "../../dependencies/custom/react-utilities";
import ValuesStore from "../../store/values-store";
import dayjs from "dayjs";
import Settings from "../../dependencies/custom/settings";
import useModal from "../../hooks/modal";
import useDelete from "../../hooks/rest_delete";
import { useNavigate } from "react-router-dom";
import CustomFunctions from "../../dependencies/custom/custom-functions";

const { Title, Text } = Typography;

const ApplicationSummary = ({
  applicant,
  parent,
  emergency,
  interview,
  files,
  currentStatus,
  bootstrap,
  onRefreshApplication,
}) => {
  const valuesStore = ValuesStore();
  const [applicableStatuses, setApplicableStatuses] = useState([]);
  const [admissionID, setAdmissionID] = useState();
  const [classApplied, setClassApplied] = useState();
  const [klasses, setKlasses] = useState();
  const [selectedStatus, setSelectedStatus] = useState();
  const [remarks, setRemarks] = useState();
  const [showInterviewScoreBtn, setShowInterviewScoreBtn] = useState(false);
  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY");
  const [interviewDate, setInterviewDate] = useState();
  const [accommodations, setAccommodations] = useState([]);
  const [streams, setStreams] = useState([]);
  const [selectedStream, setSelectedStream] = useState();
  const [selectedAllocations, setSelectedAllocations] = useState({});
  const [savedAllocations, setSavedAllocations] = useState({});
  const [admissionSaved, setAdmissionSaved] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [venuesError, setVenuesError] = useState("");
  const [venuesVersion, setVenuesVersion] = useState(0);
  const statusSavingRef = useRef(false);
  const applicantIdentity = useRef(null);
  const modal = useModal();
  const [form] = Form.useForm();
  const deleteHook = useDelete();
  const navigate = useNavigate();
  // const add = useAdd('tables_metadata', 'table_name');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [passportFileList, setPassportFileList] = useState([]);
  const [passportUploading, setPassportUploading] = useState(false);
  const showAccommodations =
    admissionSaved || selectedStatus?.toLowerCase() === "admitted";
  const isBoarding = applicant?.dayBoarding?.toLowerCase() === "boarding";
  const accommodationCategories = isBoarding
    ? ["House", "Dormitory"]
    : ["House"];
  const pendingAllocations = accommodationCategories.filter(
    (category) =>
      selectedAllocations[category] != null &&
      String(selectedAllocations[category]) !==
        String(savedAllocations[category]),
  );

  useEffect(() => {
    // console.log(applicant);

    const identity = `${applicant?.admissionID || ""}:${applicant?.id || ""}`;
    if (applicantIdentity.current !== identity) {
      applicantIdentity.current = identity;
      reset();
    }
    //fetch school classes from store
    const kls = Array.isArray(valuesStore?.getValue("klasses"))
      ? valuesStore
          .getValue("klasses")
          .map((item) => ({ value: item.id, label: item.name }))
      : [];
    setKlasses(kls);

    //normalize current status
    const cs = currentStatus?.toLowerCase();

    //get & set stream & classApplied & admissionID
    const klassApplied = applicant?.classApplied;
    setClassApplied(applicant?.classApplied);
    setAdmissionID(applicant?.admissionID);
    setSelectedStream(applicant?.streamID);

    Utilities.setPropFromStore(valuesStore, setDateFormat);

    //get streams from store
    const str = Array.isArray(valuesStore.getValue("streams"))
      ? valuesStore.getValue("streams")
      : [];
    setStreams(str);

    const statuses = Array.isArray(valuesStore.getValue("status_configs"))
      ? valuesStore.getValue("status_configs")
      : [];
    const currentStatusObject = statuses?.find(
      (v) => v?.status?.toLowerCase() === cs,
    );
    setShowInterviewScoreBtn(
      parseInt(currentStatusObject?.show_score_btn) ? true : false,
    );

    const status_transition = Array.isArray(
      valuesStore.getValue("status_transition"),
    )
      ? valuesStore.getValue("status_transition")
      : [];
    const klass_status_transition = Array.isArray(
      valuesStore.getValue("klass_status_transition"),
    )
      ? valuesStore.getValue("klass_status_transition")
      : [];

    const appStatus = klass_status_transition
      ?.filter((v) => {
        //checking if a specific class at a specific status has some specific statuses to change to
        return v?.current_status?.toLowerCase() === cs;
      })
      ?.filter((v) => v?.klass == klassApplied);

    if (appStatus?.length) {
      setApplicableStatuses(appStatus);
    } else {
      const appStatus1 = status_transition?.filter((v) => {
        //use general transition if there are no class-status-specific transitions
        return v?.current_status?.toLowerCase() === cs;
      });
      setApplicableStatuses(appStatus1);
    }

    const interviewObject = Utilities.objectFromArray(
      interview,
      "subject",
      "score",
    );
    // console.log(interviewObject)
    form.setFieldsValue(interviewObject);
    // console.log('looping')
  }, [applicant, currentStatus]);

  useEffect(() => {
    if (!showAccommodations || !applicant?.id) return;
    let current = true;
    setVenuesLoading(true);
    setVenuesError("");
    async function loadVenues() {
      try {
        const categories = isBoarding ? ["House", "Dormitory"] : ["House"];
        const responses = await Promise.all(
          categories.map((category) =>
            Utilities.fetchWithAuth(
              "GET",
              `${Settings.urls.enrollment.accommodation}?category=${category}`,
              undefined,
              navigate,
            ),
          ),
        );
        if (!current) return;
        const failed = responses.find((response) => !response?.ok);
        if (failed) {
          setVenuesError(
            Utilities.formatErrorMessage(
              { ...failed, msg: failed.data ? undefined : failed.msg },
              "Unable to load accommodation venues.",
            ),
          );
          return;
        }
        setAccommodations(
          responses.flatMap((response) =>
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          ),
        );
      } catch (error) {
        if (current)
          setVenuesError(
            error.message || "Unable to load accommodation venues.",
          );
      } finally {
        if (current) setVenuesLoading(false);
      }
    }
    loadVenues();
    return () => {
      current = false;
    };
  }, [showAccommodations, applicant?.id, isBoarding, venuesVersion, navigate]);

  const uploads = files?.filesFromDB || [];
  const categoryRules = files?.categoryRules || {};
  const passportPhoto = uploads?.find(
    (file) => file?.category === "passportPhoto",
  );

  function reset() {
    // setAdmissionID(undefined);
    setShowInterviewScoreBtn(false);
    setSelectedStatus(undefined);
    setSelectedAllocations({});
    setSavedAllocations({});
    setAdmissionSaved(false);
    setStatusError("");
  }

  const handlePreview = async (url) => {
    Utilities.openPdf(url);
  };

  function validatePassportPhoto(file) {
    const allowedExtensions = [".jpg", ".jpeg", ".png"];
    const allowedMimeTypes = ["image/jpeg", "image/png"];
    const lowerName = file?.name?.toLowerCase() || "";
    const hasAllowedExtension = allowedExtensions.some((ext) =>
      lowerName.endsWith(ext),
    );
    const hasAllowedMimeType =
      !file?.type || allowedMimeTypes.includes(file.type);

    if (!hasAllowedExtension || !hasAllowedMimeType) {
      message.error("Passport photo must be a PNG, JPG, or JPEG image");
      return Upload.LIST_IGNORE;
    }

    return false;
  }

  async function uploadPassportPhoto() {
    const file = passportFileList?.[0]?.originFileObj;

    if (!admissionID) {
      Utilities.showNotification(undefined, "No applicant has been selected");
      return;
    }

    if (!file) {
      Utilities.showNotification(
        undefined,
        "Select a passport photo to upload",
      );
      return;
    }

    setPassportUploading(true);
    const hasExistingPassportPhoto = Boolean(passportPhoto);
    const method = hasExistingPassportPhoto ? "PUT" : "POST";
    const payload = hasExistingPassportPhoto
      ? { admissionID, fileType: "passportPhoto", file }
      : { admissionID, fileType: "passportPhoto", files: file };
    const url = Settings.urls.enrollment.uploads;

    const res = await Utilities.fetchWithAuth(method, url, payload, navigate);

    if (res?.ok) {
      Utilities.showNotification(
        undefined,
        hasExistingPassportPhoto
          ? "Passport photo replaced successfully"
          : "Passport photo uploaded successfully",
        "text-success",
      );
      setPassportFileList([]);
      if (typeof onRefreshApplication === "function") {
        await onRefreshApplication();
      } else if (typeof bootstrap === "function") {
        await bootstrap();
      }
    } else {
      Utilities.showNotification(
        undefined,
        res?.data?.error ||
          res?.data?.detail ||
          res?.msg ||
          "Passport photo upload failed",
      );
    }

    setPassportUploading(false);
  }

  const confirm = async () => {
    if (
      statusSavingRef.current ||
      !applicant?.id ||
      !admissionID ||
      (!admissionSaved && !selectedStatus)
    )
      return;
    if (showAccommodations && (venuesLoading || venuesError)) return;
    // Validate selections before admitting; the assignment endpoint checks again on save.
    if (
      showAccommodations &&
      pendingAllocations.some((category) => {
        const venue = accommodations.find(
          (item) =>
            item.category === category &&
            String(item.id) === String(selectedAllocations[category]),
        );
        return (
          !venue ||
          (category === "Dormitory" && Number(venue.remainingSpace) <= 0)
        );
      })
    ) {
      setStatusError(
        "Select an available venue for each chosen accommodation category.",
      );
      return;
    }
    statusSavingRef.current = true;
    const operationIdentity = applicantIdentity.current;
    const isCurrentApplicant = () =>
      applicantIdentity.current === operationIdentity;
    setStatusSaving(true);
    setStatusError("");
    let admitted = admissionSaved;
    try {
      if (!admitted) {
        const response = await Utilities.fetchWithAuth(
          "POST",
          Settings.urls.enrollment.update_status,
          {
            admissionID,
            status: selectedStatus,
            previousStatus: currentStatus,
            remarks,
            interviewDate,
          },
          navigate,
        );
        if (!isCurrentApplicant()) return;
        if (!response?.ok) {
          setStatusError(
            Utilities.formatErrorMessage(
              { ...response, msg: response?.data ? undefined : response?.msg },
              "Unable to update admission status.",
            ),
          );
          return;
        }
        admitted = selectedStatus?.toLowerCase() === "admitted";
        if (admitted) setAdmissionSaved(true);
      }

      const saved = { ...savedAllocations };
      const failed = [];
      if (admitted) {
        for (const category of pendingAllocations) {
          let success = false;
          try {
            success = await CustomFunctions.submitAccommodation(
              "POST",
              applicant,
              selectedAllocations[category],
              applicant.id,
              navigate,
            );
          } catch {
            // Continue with the other independent slot and retain failures for retry.
          }
          if (!isCurrentApplicant()) return;
          if (success) {
            saved[category] = selectedAllocations[category];
            setSavedAllocations({ ...saved });
          } else {
            failed.push(category);
          }
        }
      }
      if (failed.length) {
        const savedNames = Object.keys(saved);
        setStatusError(
          `Admission completed. ${savedNames.length ? `${savedNames.join(" and ")} saved. ` : ""}${failed.join(" and ")} could not be saved. Review the selection and retry the remaining accommodation.`,
        );
        setVenuesVersion((version) => version + 1);
        await bootstrap?.({ keepDrawerOpen: true });
        return;
      }

      await bootstrap?.();
      if (!isCurrentApplicant()) return;
      Utilities.scrollToTop();
      reset();
      Utilities.showNotification(
        undefined,
        admitted
          ? pendingAllocations.length
            ? "Admission completed. Selected accommodation saved."
            : "Admission completed."
          : "Status updated successfully.",
        "text-success",
      );
    } catch (error) {
      if (isCurrentApplicant()) {
        setStatusError(
          `${admitted ? "Admission completed. " : ""}${error.message || "Unable to finish. Please retry."}`,
        );
      }
    } finally {
      statusSavingRef.current = false;
      setStatusSaving(false);
    }
  };

  function setSubjects() {
    if (!admissionID) {
      Utilities.showNotification(undefined, "No Applicant has been selected");
      return;
    }
    const subject = Array.isArray(valuesStore.getValue("subjects"))
      ? valuesStore.getValue("subjects")
      : [];
    const interviewSubjects = subject?.filter((v) =>
      parseInt(v?.for_interview),
    );
    const formItems = interviewSubjects?.map((v) => {
      return (
        <Form.Item
          key={v?.id}
          name={v?.name}
          label={v?.name}
          // rules={[{ required: true }]}
        >
          <Input type="number" placeholder={`Enter marks for ${v?.name}`} />
        </Form.Item>
      );
    });
    modal.setContent(
      <Form form={form} layout="vertical">
        {formItems}
      </Form>,
    );
    modal.setOpen(true);
  }

  async function handleMarksSubmit() {
    // modal.set
    await form.validateFields();
    const vals = form.getFieldsValue();
    let results = [];
    for (let subject in vals) {
      const v = { subject, score: vals[subject], admissionID };
      const res = await Utilities.fetchWithAuth(
        "POST",
        Settings?.urls?.enrollment?.interview,
        v,
      );
      results.push(res?.ok);
    }
    const allRes = results?.every((v) => v);

    if (allRes) {
      Utilities.showNotification(
        undefined,
        "Operation successful",
        "text-success",
      );
    } else {
      Utilities.showNotification(undefined, "Operation failed");
      return;
    }
    modal.setOpen(false);
  }

  async function cancelAdmission() {
    setActionLoading(true);
    const res = await Utilities.fetchWithAuth(
      "PATCH",
      Settings.urls.enrollment.admissionBulkCancel,
      { admissionIDs: [admissionID] },
      navigate,
    );
    if (res?.ok) {
      Utilities.showNotification(
        undefined,
        "Application cancelled",
        "text-success",
      );
      bootstrap();
    } else {
      Utilities.showNotification(undefined, res?.msg || "Operation failed");
    }
    setActionLoading(false);
  }

  async function deleteAdmission() {
    setActionLoading(true);
    const res = await Utilities.fetchWithAuth(
      "DELETE",
      Settings.urls.enrollment.admissionBulkDelete,
      { admissionIDs: [admissionID] },
      navigate,
    );
    if (res?.ok) {
      Utilities.showNotification(
        undefined,
        "Application deleted",
        "text-success",
      );
      bootstrap();
    } else {
      Utilities.showNotification(undefined, res?.msg || "Operation failed");
    }
    setActionLoading(false);
  }

  async function changeClass() {
    if (!classApplied) {
      Utilities.showNotification(undefined, "Please set class");
      return;
    }
    if (!applicant?.id) {
      Utilities.showNotification(
        undefined,
        "Student ID not found. Please try again",
      );
      return;
    }
    setLoading(true);
    const res = await Utilities.fetchWithAuth(
      "PATCH",
      `${Settings.urls.enrollment.student}/${applicant?.id}`,
      { classApplied, streamID: selectedStream },
    );
    if (res.ok) {
      // console.log(res?.data);
      Utilities.showNotification(
        undefined,
        res?.message || res?.data?.message,
        "text-success",
      );
    } else {
      Utilities.showNotification(undefined, res?.message || res?.data?.message);
    }
    setLoading(false);
  }

  function changeStatus(v) {
    if (statusSavingRef.current || admissionSaved) return;
    setSelectedStatus(v);
    setStatusError("");
    if (v?.toLowerCase() === "admitted") setVenuesLoading(true);
  }

  function onDateChange(date) {
    setInterviewDate(date.format("YYYY-MM-DD HH:mm"));
  }

  function disablePastDates(current) {
    // current is a dayjs object
    return current && current < dayjs().endOf("day"); // disables any date strictly before today
  }

  return (
    <div style={{ /*background: "#f5f7fa",*/ minHeight: "100vh" }}>
      {/* <Affix offsetTop={50}> */}
      <Card
        className="border border-1"
        style={{
          marginBottom: 16,
          borderRadius: 10,
          // boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          // background:'#245b79;',
          // color:'white'
        }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} sm={24}>
            <Title level={4} style={{ margin: 0 }}>
              Take Action
            </Title>
            <Text type="secondary">
              Application Code: {applicant?.application_code || "N/A"}
            </Text>
          </Col>
          <Col xs={24} sm={24}>
            <div>
              <Space wrap>
                <div className="">
                  Change Class Applied:{" "}
                  <Select
                    className="w-100"
                    value={classApplied}
                    disabled={statusSaving || admissionSaved}
                    onChange={(v) => setClassApplied(v)}
                    placeholder="Change Class Applied"
                    options={klasses}
                  />
                </div>
                <div className="">
                  Change Stream:{" "}
                  <Select
                    className="w-100"
                    value={selectedStream}
                    disabled={statusSaving || admissionSaved}
                    onChange={(v) => setSelectedStream(v)}
                    placeholder="Choose stream"
                    options={streams?.map((v) => ({
                      value: v?.id,
                      label: v?.name,
                    }))}
                  />
                </div>
                <br />
                <div className="mt-3">
                  <Tooltip title="Click to change class applied & stream">
                    <Button
                      onClick={(e) => changeClass()}
                      className="ms-2 btn-success border-0"
                      loading={loading}
                      disabled={statusSaving || admissionSaved}
                      icon={<i className="fas fa-save" />}
                    >
                      Save changes
                    </Button>
                  </Tooltip>
                </div>
              </Space>
              <Divider />
              {statusError && (
                <Alert
                  type="error"
                  showIcon
                  message={statusError}
                  style={{ marginBottom: 16 }}
                />
              )}
              {admissionSaved && (
                <Alert
                  type="info"
                  showIcon
                  message="Admission is complete. Retry the remaining accommodation here, or finish later from Housing Allocation."
                  style={{ marginBottom: 16 }}
                />
              )}
              {showAccommodations && venuesError && (
                <Alert
                  type="error"
                  showIcon
                  message={venuesError}
                  action={
                    <Button
                      onClick={() => setVenuesVersion((version) => version + 1)}
                    >
                      Retry loading
                    </Button>
                  }
                  style={{ marginBottom: 16 }}
                />
              )}
              <Space className="mb-2" wrap>
                <label className="">Change Status:</label>{" "}
                <Select
                  value={selectedStatus}
                  disabled={statusSaving || admissionSaved}
                  onChange={(v) => changeStatus(v)}
                  placeholder="Change Status"
                  options={applicableStatuses?.map((v) => ({
                    value: v?.can_change_to,
                    label: v?.can_change_to,
                  }))}
                />
                {/* <br /> */}
                {showAccommodations && (
                  <>
                    {accommodationCategories.map((category) => (
                      <div key={category}>
                        <label
                          htmlFor={`admission-${category}`}
                          style={{ marginRight: 8 }}
                        >
                          {category}:
                        </label>
                        <Select
                          id={`admission-${category}`}
                          aria-label={category}
                          style={{ width: 250, maxWidth: "100%" }}
                          value={selectedAllocations[category]}
                          allowClear={!savedAllocations[category]}
                          showSearch
                          optionFilterProp="label"
                          loading={venuesLoading}
                          disabled={
                            statusSaving ||
                            venuesLoading ||
                            !!venuesError ||
                            !!savedAllocations[category]
                          }
                          onChange={(id) =>
                            setSelectedAllocations((current) => ({
                              ...current,
                              [category]: id,
                            }))
                          }
                          placeholder={`Select ${category.toLowerCase()} (optional)`}
                          options={accommodations
                            .filter((venue) => venue.category === category)
                            .map((venue) => ({
                              value: venue.id,
                              label:
                                category === "House"
                                  ? venue.name
                                  : `${venue.name} — ${venue.remainingSpace} spaces remaining`,
                              disabled:
                                category === "Dormitory" &&
                                Number(venue.remainingSpace) <= 0 &&
                                String(venue.id) !==
                                  String(savedAllocations[category]),
                            }))}
                        />
                      </div>
                    ))}
                  </>
                )}
                {/* <br /> */}
              </Space>
              <div className="mb-2">
                {admissionID && currentStatus == "VERIFIED" && (
                  <>
                    <label className="me-3">Interview Date:</label>
                    <DatePicker
                      value={interviewDate ? dayjs(interviewDate) : null}
                      disabled={statusSaving || admissionSaved}
                      onChange={(date, dateString) =>
                        onDateChange(date, dateString)
                      }
                      // disabledDate={disablePastDates}
                      showTime={{ format: "HH:mm" }}
                      format="YYYY-MM-DD HH:mm"
                    />
                  </>
                )}
                <br />
                Remarks:{" "}
                <Input.TextArea
                  value={remarks}
                  disabled={statusSaving || admissionSaved}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={1}
                  autoSize
                />
              </div>
              <Space>
                {admissionID && showInterviewScoreBtn && (
                  <Tooltip title="Delete Scores">
                    {deleteHook.confirm(
                      `${Settings?.urls?.enrollment?.adm_interview}/${admissionID}`,
                      {},
                      "Deleting Scores?",
                      "DELETE",
                      <Button icon={<i className="fas fa-trash text-danger" />}>
                        {" "}
                        Delete Scores
                      </Button>,
                    )}
                  </Tooltip>
                )}
                {admissionID && showInterviewScoreBtn && (
                  <Button
                    onClick={(e) => setSubjects()}
                    icon={<i className="fas fa-plus" />}
                  >
                    Add Scores
                  </Button>
                )}
                {admissionID && (
                  <Popconfirm
                    title="Confirm status change"
                    description="Click OK to proceed?"
                    onConfirm={confirm}
                    disabled={
                      admissionSaved ||
                      statusSaving ||
                      !selectedStatus ||
                      (showAccommodations && (venuesLoading || !!venuesError))
                    }
                  >
                    <Button
                      type="primary"
                      icon={<i className="fas fa-save" />}
                      loading={statusSaving}
                      disabled={
                        (!admissionSaved && !selectedStatus) ||
                        (showAccommodations && (venuesLoading || !!venuesError))
                      }
                      onClick={admissionSaved ? confirm : undefined}
                    >
                      {admissionSaved
                        ? pendingAllocations.length
                          ? "Retry accommodation"
                          : "Finish admission"
                        : "Submit"}
                    </Button>
                  </Popconfirm>
                )}
                {admissionSaved && (
                  <Button
                    disabled={statusSaving}
                    onClick={async () => {
                      if (statusSavingRef.current) return;
                      await bootstrap?.();
                      reset();
                    }}
                  >
                    Finish later
                  </Button>
                )}
              </Space>
            </div>
          </Col>
        </Row>
      </Card>
      {/* </Affix> */}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={24}>
          <Card
            title="Applicant Details"
            bordered={false}
            className="mb-2 border border-1"
          >
            <Row gutter={[16, 12]}>
              <Col xs={24} sm={12}>
                <Text strong>First Name:</Text> {applicant?.firstName}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Last Name:</Text> {applicant?.lastName}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Middle Name:</Text> {applicant?.middleName}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>DOB:</Text>{" "}
                {applicant?.dob && dayjs(applicant?.dob)?.format(dateFormat)}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>AGE:</Text>{" "}
                {applicant?.dob && dayjs(applicant?.dob).isValid()
                  ? dayjs().diff(dayjs(applicant?.dob), "year")
                  : ""}
              </Col>

              <Col xs={24} sm={12}>
                <Text strong>Gender:</Text> {applicant?.gender}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Nationality:</Text> {applicant?.nationality}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Previous School:</Text> {applicant?.previousSchool}
              </Col>
              <Col xs={24} sm={12}>
                <Tag color="red">
                  {" "}
                  <Text strong>Class Applied:</Text> {applicant?.className}
                </Tag>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Day/Boarding:</Text> {applicant?.dayBoarding}
              </Col>
            </Row>
          </Card>

          <Card
            title="Parent / Guardian Details"
            bordered={false}
            className="mb-2  border border-1"
          >
            <Row gutter={[16, 12]}>
              <Col xs={24} sm={24}>
                <Text strong>Name:</Text> {parent?.parentName}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>National ID:</Text> {parent?.parentNationalIDNum}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Relationship:</Text> {parent?.relationshipName}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Phone:</Text> {parent?.phonePrimary}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Email:</Text> {parent?.email}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Occupation:</Text> {parent?.occupation}
              </Col>
              <Col span={24}>
                <Text strong>Address:</Text> {parent?.address}
              </Col>
            </Row>
          </Card>

          <Card
            title="Emergency Contact"
            className=" border border-1"
            bordered={false}
          >
            <Row gutter={[16, 24]}>
              <Col xs={24} sm={12}>
                <Text strong>Name:</Text> {emergency?.emgName}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Relation:</Text> {emergency?.emgRelName}
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Phone:</Text> {emergency?.emgPhone}
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} md={24}>
          <Card
            title="Uploaded Documents"
            className=" border border-1"
            bordered={false}
          >
            <Space className="mb-3" wrap>
              <Upload
                accept=".jpg,.jpeg,.png"
                beforeUpload={validatePassportPhoto}
                fileList={passportFileList}
                maxCount={1}
                onChange={({ fileList }) =>
                  setPassportFileList(fileList.slice(-1))
                }
              >
                <Button icon={<UploadOutlined />}>
                  {passportPhoto
                    ? "Choose Replacement Passport Photo"
                    : "Choose Passport Photo"}
                </Button>
              </Upload>
              <Button
                type="primary"
                loading={passportUploading}
                disabled={!passportFileList?.length}
                onClick={uploadPassportPhoto}
              >
                {passportPhoto
                  ? "Replace Passport Photo"
                  : "Upload Passport Photo"}
              </Button>
            </Space>
            <Row gutter={[12, 12]}>
              {uploads.map((file, i) => (
                <Col xs={12} sm={12} key={i}>
                  <Card
                    hoverable
                    size="small"
                    className="border border-1 rounded text-center"
                    onClick={() => handlePreview(file.url)}
                    cover={
                      Utilities.isPdf(file.url) ? (
                        <div
                          className="border border-1 d-flex justify-content-center"
                          style={{
                            height: 100,
                            background: "#fafafa",
                          }}
                        >
                          <FilePdfOutlined
                            style={{ fontSize: 36, color: "#cf1322" }}
                          />
                        </div>
                      ) : (
                        <Image
                          src={file.url}
                          alt={file.name}
                          height={100}
                          width="100%"
                          style={{
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                          preview={false}
                        />
                      )
                    }
                  >
                    <Text strong style={{ fontSize: 12 }}>
                      {categoryRules[file.category]?.name || file.name}
                    </Text>
                    <div>
                      <Button
                        size="small"
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handlePreview(file.url)}
                      >
                        View
                      </Button>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
      {admissionID && (
        <Card className="border border-1 mt-3" bordered={false}>
          <Space>
            <Popconfirm
              title="Cancel this application?"
              onConfirm={cancelAdmission}
            >
              <Button
                loading={actionLoading}
                icon={<i className="fas fa-ban" />}
              >
                Cancel Application
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Permanently delete this application?"
              onConfirm={deleteAdmission}
            >
              <Button
                danger
                loading={actionLoading}
                icon={<i className="fas fa-trash" />}
              >
                Delete Application
              </Button>
            </Popconfirm>
          </Space>
        </Card>
      )}
      {modal.modalJSX(handleMarksSubmit)}
    </div>
  );
};

export default ApplicationSummary;
